import { useCallback, useEffect, useMemo } from 'react';
import {
  getCustomFieldsDefaultValues,
  useConfigQuery,
  getCustomFieldsValidationSchema,
  useConfigEntryOptionsQuery,
} from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import TrackerAPI from '@buildwise/libs/trackers/apis/TrackerAPI';
import { ConfigEntryType, ConfigFieldEntryType } from 'config/config';
import { getPredefinedFieldValidation } from '../helpers/tracker.helpers';
import { useTrackerContext } from '../context';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';
import { CustomFieldValueType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { TaskType, useTrackerDetailsQuery, useTrackerTasksQuery } from '@buildwise/libs/trackers';

type UseAddTaskFormOptions = {
  taskData?: TaskType;
  trackerFieldsConfigName: string;
  isEditing: boolean;
};

export type TaskFormData = {
  [key: string]: any;
};

export const createTaskFormSchema = (customFields: ConfigEntryType[]) =>
  Yup.object().shape({
    ...getCustomFieldsValidationSchema({
      fields: customFields,
      getPredefinedFieldValidation: ({ field }) => getPredefinedFieldValidation({ field }),
    }),
  });

function useAddTaskForm({ taskData, trackerFieldsConfigName, isEditing }: UseAddTaskFormOptions) {
  const { companyId } = useUserData();
  const { activeUniqueEntries } = useConfigQuery({
    companyId,
    configName: trackerFieldsConfigName,
  });
  const {
    state: { trackerId },
    dispatch,
  } = useTrackerContext();
  const { tracker } = useTrackerDetailsQuery({
    trackerId,
    shouldFetch: false,
  });
  const { activeUniqueEntries: statusOptions } = useConfigQuery({
    companyId,
    configName: tracker?.configNameForTasksStatus,
    shouldFetch: Boolean(tracker?.configNameForTasksStatus),
  });
  const { refetch, updateTasksCache } = useTrackerTasksQuery({
    trackerId,
    page: 0,
    size: 1000,
    shouldFetch: false,
  });
  const customInputFields = useMemo(() => {
    const resultantMap = new Map([...activeUniqueEntries].map((obj) => [obj.entryCode, obj]));
    return [...resultantMap.values()];
  }, [activeUniqueEntries]);

  const getPredefinedFieldDefaultValue = useCallback(
    ({ task, field }: { task?: TaskType; field: ConfigEntryType }) => {
      switch (field.entryCode) {
        case 'status': {
          return (
            task?.status ||
            statusOptions.find((entry) => entry.referenceObject?.isDefault)?.entryCode ||
            statusOptions[0]?.entryCode ||
            ''
          );
        }
        default: {
          return null;
        }
      }
    },
    [statusOptions],
  );
  const getDefaultValues = useCallback(() => {
    const fieldsDefaultValues = getCustomFieldsDefaultValues({
      data: taskData,
      fields: customInputFields,
      getPredefinedFieldDefaultValue: ({ field }) =>
        getPredefinedFieldDefaultValue({ task: taskData, field }),
    });
    return { ...fieldsDefaultValues, relatedRecords: taskData?.relatedRecords || [] };
  }, [taskData, customInputFields, getPredefinedFieldDefaultValue]);

  const taskForm = useForm<TaskFormData>({
    resolver: yupResolver(createTaskFormSchema(customInputFields)),
    defaultValues: getDefaultValues(),
  });

  const {
    formState: { isSubmitting },
    handleSubmit,
    reset,
  } = taskForm;

  const handleSave = useCallback(
    async (values: TaskFormData) => {
      try {
        const payload = {
          ...values,
          companyId,
          trackerId,
        };

        let response;

        const customFieldsValues: Record<string, CustomFieldValueType> = {};
        const articleKeys = Object.keys(taskData || {});
        customInputFields.forEach((field) => {
          if (
            Array.isArray(values[field.entryCode])
              ? values[field.entryCode].length > 0
              : values[field.entryCode]
          ) {
            if (
              (articleKeys.includes(field.entryCode) ||
                field.entryType?.toUpperCase() !== ConfigFieldEntryType.CUSTOM) &&
              field.entryCode !== 'attachments'
            ) {
              payload[field.entryCode] = values[field.entryCode];
            } else {
              if (field.entryType?.toUpperCase() === ConfigFieldEntryType.CUSTOM) {
                customFieldsValues[field.entryCode] = {
                  fieldName: field.entryCode,
                  fieldValue: values[field.entryCode],
                  type: field.referenceObject?.type || '',
                  configRefName: field.referenceObject?.configRefName || '',
                  label: field.referenceObject?.label || field.entryDescription,
                  color: field.referenceObject?.color,
                };
              }
            }
          }
        });
        payload['customFields'] = customFieldsValues;

        if (isEditing) {
          response = await TrackerAPI.editTask(companyId, taskData?.id ?? '', payload);
          updateTasksCache(response.data.tasks[0], 'update');
        } else {
          response = await TrackerAPI.addTask(companyId, payload);
          updateTasksCache(response.data.tasks[0], 'add');
        }

        dispatch(({ state }) => ({
          payload: {
            ...state,
            selectedTask: undefined,
            isOpenedTaskForm: false,
          },
        }));

        // refetch();
        toast.success(`Task ${isEditing ? 'Updated' : 'Created'} Successfully`);
      } catch (error) {
        toast.error(catchErrorMessage(error));
      }
    },
    [isEditing, companyId, trackerId, taskData?.id, dispatch, refetch],
  );

  const onSubmit = handleSubmit(handleSave);

  useEffect(() => {
    reset(getDefaultValues(), {
      keepTouched: true,
    });
  }, [taskData, getDefaultValues, reset]);

  return {
    taskForm,
    isSubmitting,
    taskFields: customInputFields,
    onSubmit,
    resetTaskForm: reset,
  };
}

export default useAddTaskForm;
